import {mapValueKeys} from '@shared/helpers/object'

import {DiscountCouponType, DiscountCoupon, DeliveryFeeClass, CouponProviderType} from '@web/core/constants'
import {DeliveryGroupState} from '@web/slices/deliveryGroup/slice'
import {
    OrderSheetCoupon,
    OrderSheetCouponState,
    OrderSheetDiscount,
    OrderSheetDiscountItem,
} from '@web/slices/orderSheetCoupon/slice'

import {FetchSubscriptionPayload} from '../../actions/subscription/interfaces'

type CouponPayload = FetchSubscriptionPayload['subscriptionViewResult']['coupon']
type DeliveryPayload = FetchSubscriptionPayload['subscriptionViewResult']['delivery']

export const getProductCouponType = ({
    discountType,
    providerType,
}: {
    discountType: CouponPayload['discountsByItemId'][string][number]['discountType']
    providerType: CouponProviderType
}): DiscountCouponType => {
    const isSeller = providerType === 'SELLER'
    switch (discountType) {
        case 'SELLER_IMMEDIATE_DISCOUNT':
            return DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT
        case 'MULTI_PURCHASE_DISCOUNT':
            return DiscountCoupon.MULTI_PURCHASE_DISCOUNT
        case 'PRODUCT_DISCOUNT':
            return isSeller ? DiscountCoupon.SELLER_PRODUCT_DISCOUNT : DiscountCoupon.MANAGER_PRODUCT_DISCOUNT
        case 'DUPLICATE_PRODUCT_DISCOUNT':
            return DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT
        case 'STORE_DISCOUNT':
            return DiscountCoupon.STORE_DISCOUNT
        case 'SUBSCRIPTION_DISCOUNT':
            return DiscountCoupon.SUBSCRIPTION_DISCOUNT
    }
    return DiscountCoupon.ORDER_DISCOUNT
}

export const convertProductCoupon = (
    coupon: CouponPayload['selectedProductCouponListsByOrderSheetItemId'],
): OrderSheetCouponState['product']['couponsByOrderSheetItemId'] => {
    return mapValueKeys((couponItems) => {
        return couponItems.map((item) => {
            return {
                ...item,
                couponType: getProductCouponType({
                    discountType: item.couponDiscountType,
                    providerType: item.couponProviderType,
                }),
                exclusiveCouponType: item.exclusiveCouponType ?? undefined,
                disabled: false,
            }
        })
    }, coupon)
}

export const convertImmediateCoupon = (
    coupon: CouponPayload['selectedImmediateDiscountListsByOrderSheetItemId'],
): OrderSheetCouponState['product']['immediateCouponsByOrderSheetItemId'] => {
    return mapValueKeys((couponItems) => {
        return couponItems.map((item) => {
            return {
                ...item,
                couponType: DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT,
            }
        })
    }, coupon)
}

export const convertStoreCoupon = (
    coupon: CouponPayload['storeCoupons']['storeCouponListsByMerchantNo'][number][number],
): OrderSheetCoupon => {
    return {
        ...coupon,
        couponType: DiscountCoupon.STORE_DISCOUNT,
        exclusiveCouponType: coupon.exclusiveCouponType ?? undefined,
        disabled: false,
    }
}

export const convertDeliveryFeeCoupon = (
    coupon: CouponPayload['selectedDeliveryFeeCouponsByDeliveryGroupKey'][string],
): OrderSheetCoupon => {
    return {
        ...coupon,
        exclusiveCouponType: coupon.exclusiveCouponType ?? undefined,
        couponType: DiscountCoupon.DELIVERY_DISCOUNT,
        disabled: false,
    }
}

export const convertDiscountInfo = (
    discountItem: CouponPayload['deliveryFeeDiscountsByDeliveryGroupKey'][string],
): OrderSheetDiscountItem => {
    return {
        ...discountItem,
        validityPeriodEndDateTime: discountItem.validityPeriodEndDateTime ?? undefined,
        multiPurchaseMinOrderValue: discountItem.multiPurchaseMinOrderValue ?? undefined,
        multiPurchaseUnitType: discountItem.multiPurchaseUnitType ?? undefined,
        couponType: getProductCouponType({
            discountType: discountItem.discountType,
            providerType: discountItem.discountProviderType,
        }),
    }
}

export const getDiscountInfo = (coupon: CouponPayload): OrderSheetDiscount => {
    return {
        productItem: mapValueKeys((discounts) => discounts.map(convertDiscountInfo), coupon.discountsByItemId),
        deliveryItem: mapValueKeys(
            (discount) => convertDiscountInfo(discount),
            coupon.deliveryFeeDiscountsByDeliveryGroupKey,
        ),
    }
}

export function convertDeliveryGroupsByKey(
    deliveryGroupsByKey: DeliveryPayload['deliveryGroupsByKey'],
): DeliveryGroupState['deliveryGroupsByKey'] {
    return mapValueKeys((deliveryGroupItem) => {
        return {
            additionalDeliveryFee: deliveryGroupItem.additionalDeliveryFee,
            deliveryFee: deliveryGroupItem.deliveryFee,
            deliveryFeePayType: deliveryGroupItem.deliveryFeePayType ?? DeliveryFeeClass.FREE,
            deliveryFeeDiscountAmount: null,
            deliveryPolicy: {
                baseFee: deliveryGroupItem.deliveryPolicy.baseFee,
                deliveryArea2ExtraFee: deliveryGroupItem.deliveryPolicy.deliveryArea2ExtraFee,
                deliveryArea3ExtraFee: deliveryGroupItem.deliveryPolicy.deliveryArea3ExtraFee,
                deliveryAreaType: deliveryGroupItem.deliveryPolicy.deliveryAreaType,
                deliveryFeeClassType: deliveryGroupItem.deliveryPolicy.deliveryFeeClassType,
                deliveryFeePayType: deliveryGroupItem.deliveryPolicy.deliveryFeePayType ?? DeliveryFeeClass.FREE,
                deliveryMethodType: deliveryGroupItem.deliveryPolicy.deliveryMethodType,
                freeConditionalAmount: deliveryGroupItem.deliveryPolicy.freeConditionalAmount,
                repeatQuantity: deliveryGroupItem.deliveryPolicy.repeatQuantity,
                secondBaseQuantity: deliveryGroupItem.deliveryPolicy.secondBaseQuantity,
                secondExtraFee: deliveryGroupItem.deliveryPolicy.secondExtraFee,
                thirdBaseQuantity: deliveryGroupItem.deliveryPolicy.thirdBaseQuantity,
                thirdExtraFee: deliveryGroupItem.deliveryPolicy.thirdExtraFee,
                additionalFeeMessage: null,
            },
        }
    }, deliveryGroupsByKey)
}
